/**
 * Format-independent machine stitch sequence and the sequencer that builds it from
 * per-object stitch runs. This is the single source for the simulator, statistics and all
 * file exporters.
 */
import { t } from '../../i18n/i18n';
import { type Vec, dist, lerp, normalize, scale, sub, add } from '../geometry/vec';
import type { Issue } from '../model/issues';
import type { Design, EmbObject, ThreadColor } from '../model/types';
import type { ObjectStitches } from './types';

export const CMD = {
  STITCH: 0,
  JUMP: 1,
  TRIM: 2,
  COLOR_CHANGE: 3,
  STOP: 4,
  END: 5,
} as const;
export type Command = (typeof CMD)[keyof typeof CMD];
/** English command names; translate with t() for display. */
export const CMD_NAMES = ['stitch', 'jump', 'trim', 'colour change', 'stop', 'end'] as const;

/**
 * Command semantics (same as most embroidery libraries):
 *  STITCH       needle moves to (x, y) and penetrates.
 *  JUMP         frame moves to (x, y) without penetrating.
 *  TRIM         thread is cut at the current position (x, y = current).
 *  COLOR_CHANGE the machine switches to the next thread (x, y = current).
 *  STOP         machine pauses (x, y = current).
 *  END          end of design.
 */
export interface StitchSequence {
  count: number;
  /** Positions in mm, design coordinates. */
  xs: Float64Array;
  ys: Float64Array;
  cmds: Uint8Array;
  /** Index into design.objects, -1 for sequencer-generated connections. */
  objs: Int32Array;
  /** Index into `threads` (colour block). */
  blocks: Int32Array;
  /** One thread per colour block in sewing order. */
  threads: ThreadColor[];
  /** Ranges [start, end) in the sequence for each object, in sewing order. */
  objectRanges: { objectId: string; index: number; start: number; end: number }[];
}

class SequenceBuilder {
  xs: number[] = [];
  ys: number[] = [];
  cmds: number[] = [];
  objs: number[] = [];
  blocks: number[] = [];
  obj = -1;
  block = 0;
  push(p: Vec, cmd: Command): void {
    this.xs.push(p.x);
    this.ys.push(p.y);
    this.cmds.push(cmd);
    this.objs.push(this.obj);
    this.blocks.push(this.block);
  }
  get length(): number {
    return this.cmds.length;
  }
  last(): Vec | null {
    const n = this.cmds.length;
    return n ? { x: this.xs[n - 1], y: this.ys[n - 1] } : null;
  }
  /** Last penetration position and the one before (for tie-off direction). */
  lastStitches(): [Vec, Vec] | null {
    let a: Vec | null = null;
    for (let i = this.cmds.length - 1; i >= 0; i--) {
      if (this.cmds[i] !== CMD.STITCH) {
        if (a) break;
        continue;
      }
      const p = { x: this.xs[i], y: this.ys[i] };
      if (!a) a = p;
      else if (dist(a, p) > 0.05) return [p, a];
    }
    return null;
  }
}

const TIE_LENGTH = 0.5;

function tieStitches(at: Vec, toward: Vec): Vec[] {
  const d = dist(at, toward);
  if (d < 0.05) return [];
  const step = Math.min(TIE_LENGTH, Math.max(0.3, d));
  const u = normalize(sub(toward, at));
  const q = add(at, scale(u, step));
  return [q, at, q, at];
}

export interface SequenceOptions {
  /** Emit lock stitches (honours per-object tieIn/tieOff flags). */
  ties?: boolean;
}

/**
 * Builds the sewing sequence. Hidden objects are left out (not sewn); locked objects are included
 * (locking only protects against accidental editing).
 * Objects whose generation failed (no runs) are skipped and reported.
 */
export function buildSequence(
  design: Design,
  stitches: (ObjectStitches | undefined)[],
  options: SequenceOptions = {},
): { sequence: StitchSequence; issues: Issue[] } {
  const ties = options.ties ?? true;
  const m = design.machine;
  const issues: Issue[] = [];
  const b = new SequenceBuilder();
  const threads: ThreadColor[] = [];
  const ranges: StitchSequence['objectRanges'] = [];
  const paletteById = new Map(design.palette.map((t) => [t.id, t]));

  let currentColor: string | null = null;
  let prevObj = null as EmbObject | null;
  let threadCut = true; // true at design start, after trims and colour changes

  const pushStitchSafe = (to: Vec): void => {
    // subdivide stitches longer than the machine limit
    const from = b.last();
    if (from) {
      const d = dist(from, to);
      if (d > m.maxStitchLength) {
        const n = Math.ceil(d / m.maxStitchLength);
        for (let k = 1; k < n; k++) b.push(lerp(from, to, k / n), CMD.STITCH);
      }
    }
    b.push(to, CMD.STITCH);
  };

  const tieOff = (allowed: boolean): void => {
    if (!ties || !allowed) return;
    const ls = b.lastStitches();
    if (!ls) return;
    const [before, at] = ls;
    for (const p of tieStitches(at, before)) b.push(p, CMD.STITCH);
  };

  const startRun = (run: Vec[], allowTie: boolean): void => {
    // after a jump or at design start: penetrate at the landing point, then lock
    const s = run[0];
    b.push(s, CMD.STITCH);
    if (ties && allowTie && run.length > 1) {
      const next = run.find((p) => dist(p, s) > 0.05);
      if (next) for (const p of tieStitches(s, next)) b.push(p, CMD.STITCH);
    }
    for (let i = 1; i < run.length; i++) pushStitchSafe(run[i]);
    threadCut = false;
  };

  /** Moves from the current position to `to` and sews `run` (to === run[0]). */
  const connectAndSew = (run: Vec[], tieOffAllowed: boolean, tieInAllowed: boolean): void => {
    const from = b.last();
    if (!from || threadCut) {
      b.push(run[0], CMD.JUMP);
      startRun(run, tieInAllowed);
      return;
    }
    const d = dist(from, run[0]);
    if (d <= m.connectStitchMax) {
      if (d > 0.05) pushStitchSafe(run[0]);
      for (let i = 1; i < run.length; i++) pushStitchSafe(run[i]);
      return;
    }
    if (d <= m.trimThreshold) {
      b.push(run[0], CMD.JUMP);
      b.push(run[0], CMD.STITCH);
      for (let i = 1; i < run.length; i++) pushStitchSafe(run[i]);
      return;
    }
    tieOff(tieOffAllowed);
    b.push(b.last()!, CMD.TRIM);
    threadCut = true;
    b.push(run[0], CMD.JUMP);
    startRun(run, tieInAllowed);
  };

  design.objects.forEach((obj, index) => {
    if (!obj.visible) return;
    const st = stitches[index];
    const runs = (st?.runs ?? []).filter((r) => r.points.length > 0);
    if (!runs.length) return;
    const thread = paletteById.get(obj.colorId);
    if (!thread) {
      issues.push({ severity: 'error', code: 'missing-color', objectId: obj.id, message: t('“{name}” uses a colour that is not in the palette. Choose a new colour.', { name: obj.name }) });
      return;
    }
    if (currentColor !== obj.colorId) {
      if (currentColor !== null) {
        b.obj = -1;
        tieOff(prevObj?.tieOff ?? true);
        b.push(b.last()!, CMD.TRIM);
        b.push(b.last()!, CMD.COLOR_CHANGE);
        b.block++;
        threadCut = true;
      }
      threads.push(thread);
      currentColor = obj.colorId;
    }
    b.obj = index;
    const start = b.length;
    runs.forEach((run, ri) => {
      const tieOffAllowed = ri === 0 ? (prevObj?.tieOff ?? true) : obj.tieOff;
      connectAndSew(run.points, tieOffAllowed, obj.tieIn);
    });
    ranges.push({ objectId: obj.id, index, start, end: b.length });
    prevObj = obj;
  });

  if (b.length) {
    b.obj = -1;
    tieOff(prevObj?.tieOff ?? true);
    b.push(b.last()!, CMD.TRIM);
    b.push(b.last()!, CMD.END);
  } else {
    b.push({ x: 0, y: 0 }, CMD.END);
  }

  const n = b.length;
  const sequence: StitchSequence = {
    count: n,
    xs: Float64Array.from(b.xs),
    ys: Float64Array.from(b.ys),
    cmds: Uint8Array.from(b.cmds),
    objs: Int32Array.from(b.objs),
    blocks: Int32Array.from(b.blocks),
    threads,
    objectRanges: ranges,
  };
  return { sequence, issues };
}

export function emptySequence(): StitchSequence {
  return {
    count: 0,
    xs: new Float64Array(0),
    ys: new Float64Array(0),
    cmds: new Uint8Array(0),
    objs: new Int32Array(0),
    blocks: new Int32Array(0),
    threads: [],
    objectRanges: [],
  };
}
